import { queryOptions } from '@tanstack/react-query';

import { gqlFetcher, graphql } from '#features/api';
import { FilterInput, FilterOperator, OrderInput } from '#features/api/types';
import { ApiError } from '#features/api/utils/ApiError';
import { PostDeleteReason } from '#features/deletion/types';
import { notePushWorthyAction } from '#features/notifications/utils/pushPrompt';
import { isDefined } from '#utils/isDefined';
import { PostInput } from '../types';
import { postFragment, postOgFragment } from './fragments';

const getPostByIdDoc = graphql(
  `
    query Post($id: UUID!) {
      post {
        byId(id: $id) {
          ...PostFragment
          settingsIds
        }
      }
    }
  `,
  [postFragment]
);

export const getPostById = (input: { id: string }) =>
  gqlFetcher(getPostByIdDoc, input).then((data) => data.post.byId);

/**
 * Lean query for the OG-image generator. Uses a named fragment spread
 * (PostOgFragment) so the backend sees it as a FragmentDefinition, not an
 * InlineFragment (RequestContextInjector ClassCasts the latter to Field).
 * The fragment skips auth-gated resolvers (discordVoiceChannel etc.) so
 * the anonymous SSR OG path doesn't take the whole response to 401.
 */
const getPostForOgDoc = graphql(
  `
    query PostForOg($id: UUID!) {
      post {
        byId(id: $id) {
          ...PostOgFragment
        }
      }
    }
  `,
  [postOgFragment]
);

export const getPostForOg = (input: { id: string }) =>
  gqlFetcher(getPostForOgDoc, input).then((data) => data.post.byId);

export const getPostByIdQueryOptions = (input: { id: string }) =>
  queryOptions({
    queryKey: ['post', input.id],
    queryFn: () => getPostById(input),
    retry: (failureCount, error) => {
      if (error instanceof ApiError) {
        return error.status === 0 && failureCount < 2;
      }

      if (error instanceof Error && error.name === 'TypeError') {
        return failureCount < 2;
      }

      return false;
    },
    retryDelay: (attemptIndex) => Math.min(500 * 2 ** attemptIndex, 2000),
  });

const getCanCreatePostDoc = graphql(`
  query CanCreatePost($gameId: UUID!) {
    post {
      myPostByGameId(gameId: $gameId) {
        id
        updatedAt
      }
    }
  }
`);

export const getCanCreatePost = (input: { gameId: string }) =>
  gqlFetcher(getCanCreatePostDoc, input).then((data) => data.post.myPostByGameId);

export const getCanCreatePostQueryOptions = (input: { gameId: string }) =>
  queryOptions({
    queryKey: ['can-create-post', input.gameId],
    queryFn: () => getCanCreatePost(input),
  });

const getMyPostGameIdsDoc = graphql(`
  query MyPostGameIds {
    post {
      myGameIds
    }
  }
`);

export const getMyPostGameIds = () =>
  gqlFetcher(getMyPostGameIdsDoc, {})
    .then((data) => new Set(data.post.myGameIds?.filter(isDefined) ?? []))
    // Graceful fallback if backend doesn't have myGameIds yet (deploy ordering).
    // Returns empty set → tags hidden; backend still enforces duplicate post rule on create.
    .catch(() => new Set<string>());

export const getMyPostGameIdsQueryOptions = () =>
  queryOptions({
    queryKey: ['my-post-game-ids'],
    // В кеше — JSON-массив. Set существует только у потребителя; иначе после перезапуска
    // JSON.stringify оставлял бы {}, а .has в выборе игры падал. Ошибка сети не становится
    // успешным пустым списком и не затирает сохранённые отметки существующих заявок.
    queryFn: () =>
      gqlFetcher(getMyPostGameIdsDoc, {}).then(
        (data) => data.post.myGameIds?.filter(isDefined) ?? []
      ),
    select: (ids) => new Set(ids),
    staleTime: 60_000,
  });

// `preview` returns the PostResult union and the OG handler narrows on
// __typename, which only arrives if selected here: PostFragment doesn't
// include it and gql.tada adds nothing at runtime. A plain __typename field
// is safe — an `... on Post` inline fragment is not (see postOgFragment).
const getPostPreviewDoc = graphql(
  `
    query PostPreview($input: PostInput!, $userId: UUID!) {
      post {
        preview(input: $input, userId: $userId) {
          __typename
          ...PostFragment
        }
      }
    }
  `,
  [postFragment]
);

export const getPostPreview = (input: { input: PostInput; userId: string }) =>
  gqlFetcher(getPostPreviewDoc, input).then((data) => data.post.preview);

const getPostsDoc = graphql(
  `
    query Posts($offset: Int, $limit: Int, $orders: [OrderInput], $filters: [FilterInput]) {
      post {
        list(offset: $offset, limit: $limit, orders: $orders, filters: $filters) {
          posts {
            ...PostFragment
          }
          hasMore
          totalCount
        }
      }
    }
  `,
  [postFragment]
);

export type GetPostsInput = {
  offset: number;
  limit: number;
  orders?: OrderInput[];
  filters?: FilterInput[];
};

const createPlainPostFilter = (): FilterInput => ({
  field: 'session',
  operator: FilterOperator.EQUALS,
  value: false,
  filters: [],
});

export const withPlainPostFilters = (filters: FilterInput[] = []) => [
  createPlainPostFilter(),
  ...filters,
];

export const getPosts = (input: GetPostsInput) =>
  gqlFetcher(getPostsDoc, input).then((data) => ({
    records: data.post.list?.posts?.filter(isDefined),
    hasMore: data.post.list?.hasMore,
    totalCount: data.post.list?.totalCount,
  }));

/**
 * Лента по курсору: следующая страница просится "после вот этой заявки", а не "пропусти N", поэтому
 * новая или поднятая заявка между запросами не сдвигает страницы и не даёт повторов. Курсор -
 * строка шлюза, её не разбирают и не собирают: берут `nextCursor` ответа и шлют обратно в `after`.
 * Счётчика всех совпадений тут нет; он остаётся у `getPosts`.
 */
const getPostsByCursorDoc = graphql(
  `
    query PostsByCursor(
      $limit: Int
      $after: String
      $orders: [OrderInput]
      $filters: [FilterInput]
    ) {
      post {
        listByCursor(limit: $limit, after: $after, orders: $orders, filters: $filters) {
          posts {
            ...PostFragment
          }
          hasMore
          nextCursor
        }
      }
    }
  `,
  [postFragment]
);

export type GetPostsByCursorInput = {
  /** `nextCursor` предыдущей страницы; `null` для первой. */
  after: string | null;
  limit: number;
  orders?: OrderInput[];
  filters?: FilterInput[];
};

export const getPostsByCursor = (input: GetPostsByCursorInput) =>
  gqlFetcher(getPostsByCursorDoc, input).then((data) => ({
    records: data.post.listByCursor?.posts?.filter(isDefined),
    hasMore: data.post.listByCursor?.hasMore,
    // null у последней страницы: наружу отдаём undefined, чтобы "курсора нет" читалось одинаково.
    nextCursor: data.post.listByCursor?.nextCursor ?? undefined,
  }));

export const getPlainPosts = (input: GetPostsInput) =>
  getPosts({
    ...input,
    filters: withPlainPostFilters(input.filters),
  });

const getUniqueUserPostsDoc = graphql(
  `
    query UniqueUserPosts(
      $offset: Int
      $limit: Int
      $orders: [OrderInput]
      $filters: [FilterInput]
      $uniqueByUser: Boolean
    ) {
      post {
        list(
          offset: $offset
          limit: $limit
          orders: $orders
          filters: $filters
          uniqueByUser: $uniqueByUser
        ) {
          posts {
            ...PostFragment
          }
          hasMore
          totalCount
        }
      }
    }
  `,
  [postFragment]
);

export type GetUniqueUserPostsInput = GetPostsInput & { uniqueByUser?: boolean };

export const getUniqueUserPosts = (input: GetUniqueUserPostsInput) =>
  gqlFetcher(getUniqueUserPostsDoc, input).then((data) => ({
    records: data.post.list?.posts?.filter(isDefined),
    hasMore: data.post.list?.hasMore,
    totalCount: data.post.list?.totalCount,
  }));

const getPostsCountDoc = graphql(`
  query PostsCount($filters: [FilterInput]) {
    post {
      list(offset: 0, limit: 0, filters: $filters) {
        totalCount
      }
    }
  }
`);

export const getPostsCount = (filters: FilterInput[]) =>
  gqlFetcher(getPostsCountDoc, { filters }).then((data) => data.post.list?.totalCount ?? 0);

/**
 * Счётчик для кнопки «Показать N» в мобильной шторке фильтров. limit: 0 — тело списка
 * не приезжает, только totalCount, поэтому запрос дешёвый и его не жалко дёргать
 * на каждое изменение фильтра, пока шторка открыта.
 */
export const getPostsCountQueryOptions = (filters: FilterInput[]) =>
  queryOptions({
    queryKey: ['posts-count', filters],
    queryFn: () => getPostsCount(filters),
    staleTime: 30_000,
  });

const getPostCountsByGamesDoc = graphql(`
  query PostCountsByGames($gameIds: [UUID!]!) {
    post {
      postCountsByGames(gameIds: $gameIds) {
        gameId
        count
      }
    }
  }
`);

export const getGamePostCountsQueryOptions = (gameIds: string[]) =>
  queryOptions({
    queryKey: ['game-post-counts', gameIds],
    queryFn: async () => {
      const data = await gqlFetcher(getPostCountsByGamesDoc, { gameIds });
      const counts = data.post.postCountsByGames ?? [];
      return Object.fromEntries(counts.map((item) => [item.gameId, item.count])) as Record<
        string,
        number
      >;
    },
    staleTime: 60_000,
  });

const getFavouritePostsDoc = graphql(
  `
    query FavouritePosts(
      $offset: Int
      $limit: Int
      $orders: [OrderInput]
      $filters: [FilterInput]
    ) {
      favourite {
        list(offset: $offset, limit: $limit, orders: $orders, filters: $filters) {
          favourites {
            data {
              ...PostFragment
            }
          }
          hasMore
          totalCount
        }
      }
    }
  `,
  [postFragment]
);

export const getFavouritePosts = (input: GetPostsInput) =>
  gqlFetcher(getFavouritePostsDoc, input).then((data) => ({
    records: data.favourite.list?.favourites?.filter(isDefined),
    hasMore: data.favourite.list?.hasMore,
    totalCount: data.favourite.list?.totalCount,
  }));

export const getFavouritePlainPosts = (input: GetPostsInput) =>
  getFavouritePosts({
    ...input,
    filters: withPlainPostFilters(input.filters),
  });

const createPostDoc = graphql(`
  mutation CreatePost($input: PostInput!) {
    post {
      create(input: $input) {
        __typename
        ... on Post {
          id
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
        ... on InputError {
          errors {
            messagesErrors
            name
          }
        }
      }
    }
  }
`);

export const createPost = (input: PostInput) =>
  gqlFetcher(createPostDoc, { input }).then((data) => {
    // Заявка опубликована — человек ждёт откликов, и это повод спросить про уведомления.
    if (data.post.create?.__typename === 'Post') {
      notePushWorthyAction();
    }
    return data.post.create;
  });

const editPostDoc = graphql(`
  mutation EditPost($input: PostInput!) {
    post {
      update(input: $input) {
        __typename
        ... on GlobalError {
          errorCode
          errorMessage
        }
        ... on InputError {
          errors {
            messagesErrors
            name
          }
        }
        ... on Post {
          id
        }
      }
    }
  }
`);

export const editPost = (input: PostInput) =>
  gqlFetcher(editPostDoc, { input }).then((data) => data.post.update);

const bumpPostDoc = graphql(`
  mutation BumpPost($id: UUID!) {
    post {
      bump(id: $id) {
        __typename
        ... on Operation {
          status
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
      }
    }
  }
`);

export const bumpPost = (input: { id: string }) =>
  gqlFetcher(bumpPostDoc, input).then((data) => data.post.bump);

const setPostMatchAlertsDoc = graphql(`
  mutation SetPostMatchAlerts($id: UUID!, $enabled: Boolean!) {
    post {
      matchAlerts(id: $id, enabled: $enabled) {
        __typename
        ... on Operation {
          status
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
      }
    }
  }
`);

/** Подписка заявки на совпадения: включить (продлевает на неделю) или выключить. */
export const setPostMatchAlerts = (input: { id: string; enabled: boolean }) =>
  gqlFetcher(setPostMatchAlertsDoc, input).then((data) => data.post.matchAlerts);

/*
 * Статус подписки отдельным запросом, а не полями общего фрагмента заявки: фрагмент стоит в
 * каждом списке, и до выкатки шлюза с новыми полями он ронял бы все экраны с заявками разом.
 * Так до выкатки не работает только этот запрос, и только там, где он нужен.
 */
const postMatchAlertsDoc = graphql(`
  query PostMatchAlerts($id: UUID!) {
    post {
      byId(id: $id) {
        id
        matchAlertsEnabled
        matchAlertsExpiresAt
      }
    }
  }
`);

export const postMatchAlertsQueryOptions = (input: { id: string }) =>
  queryOptions({
    queryKey: ['post-match-alerts', input.id],
    queryFn: () => gqlFetcher(postMatchAlertsDoc, input).then((data) => data.post.byId),
    staleTime: 30_000,
    retry: false,
  });

const deletePostDoc = graphql(`
  mutation DeletePost($id: UUID!, $reason: PostDeleteReason, $reasonText: String) {
    post {
      delete(id: $id, reason: $reason, reasonText: $reasonText) {
        __typename
        ... on Operation {
          status
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
      }
    }
  }
`);

export const deletePost = (input: { id: string; reason?: PostDeleteReason; reasonText?: string }) =>
  gqlFetcher(deletePostDoc, input).then((data) => data.post.delete);
